/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useCloseAccountMutation } from 'api/account'
import { useAppSelector } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    form: RefObject<FormikProps<{ confirm_termination: boolean, password: string, confirm_password: string }>>
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function CloseAccountForm({ form }: Props) {
    const [closeAccount, { isError }] = useCloseAccountMutation()
    const { appSettings: { provider: { application_name } } } = useAppSelector((state) => state.app)

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if (isError && form.current) {
            form.current.setSubmitting(false)
            form.current.resetForm()
        }
    }, [form, isError])

    /*   RENDER COMPONENT
    **********************************************************************************************************/
    return (
        <Formik
            innerRef={form}
            initialValues={{
                confirm_termination: false,
                password: '',
                confirm_password: ''
            }}
            validationSchema={
                Yup.object().shape({
                    confirm_termination: Yup.bool().required('Required'),
                    password: Yup.string()
                        .required('Required'),
                    confirm_password: Yup.string()
                        .oneOf([Yup.ref('password'), null], 'Password must match')
                        .required('Required')
                })
            }
            onSubmit={({ password }) => {
                closeAccount({ password })
            }}
        >
            {({ isSubmitting }) => {
                return (
                    <Form>
                        <Field
                            id={'password'}
                            label={'Password'}
                            name={'password'}
                            type={'password'}
                            autoComplete={'current-password'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            id={'confirm_password'}
                            label={'Confirm Password'}
                            name={'confirm_password'}
                            type={'password'}
                            autoComplete={'confirm-password'}
                            disabled={isSubmitting}
                            component={NXUIForm.InputField}
                        />
                        <Field
                            id={'confirm_termination'}
                            label={`
                                You agree to have this account closed and all access removed from ${application_name}  
                                and all ordering abilities for this account.
                                All existing accounts credit, SMS credits, invoices, emails and tickets will become inaccessible
                                by confirming this closure.
                                You will be logged out of ${application_name} once you submit this form.
                            `}
                            name={'confirm_termination'}
                            type={'checkbox'}
                            component={NXUIForm.CheckboxField}
                        />
                    </Form>
                )
            }}
        </Formik>
    )
}
